import {Component, inject, Output, Input, EventEmitter, OnInit, OnDestroy, ViewChild} from '@angular/core';
import {Brand, Calcdebt} from "../../interfaces/account-data";
import {Router} from "@angular/router";
import {HttpClient} from "@angular/common/http";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {BaseService} from "../../services/base.service";
import {cloneDeep} from 'lodash';
import {OrganizationsService} from "../../services/organizations.service";
import {Organization} from "../../interfaces/organization";
import {BuCalculationsBalance} from "../../interfaces/bu-accounts";
import {ModalBuCalculationsComponent} from "./modal-bu-calculations/modal-bu-calculations.component";
import {Store} from "@ngrx/store";
import {CharacteristicItem, Characteristics, CharacteristicsData} from "../../interfaces/characteristics";
import {MenuService} from "../../services/menu.service";

@Component({
  selector: 'app-categories-menu',
  templateUrl: './categories-menu.component.html',
  styleUrls: ['./categories-menu.component.scss', '../../pages/account/account.component.scss']
})
export class CategoriesMenuComponent extends BaseService implements OnInit, OnDestroy {
  @Input() headCategoryName: string;
  @Input() subHeadCategoryName: string;
  @Input() headCategoryNameColored: string;
  @Input() has_cards: boolean;
  @Input() categoryHeadShow: string;
  @Input() categoriesSortedDeep;
  @Input() brandsSorted: Brand[];
  @Input() urlParams;
  @Input() calcDebt: Calcdebt;

  @Output() buildCategoriesMenuGoodsData = new EventEmitter();
  @Output() showModalPaymentRef = new EventEmitter();

  @ViewChild(ModalBuCalculationsComponent) modalBuCalculationsRef: ModalBuCalculationsComponent;

  private router: Router = inject(Router);
  private http = inject(HttpClient);
  private organizationsService = inject(OrganizationsService);
  private menuService = inject(MenuService);
  private store: Store<{ haveGoodsStore: boolean }> = inject(Store);

  public isDestroyed: Subject<boolean> = new Subject<boolean>();
  public activeOrganization: Organization;
  public buBalance: number | null = null;

  //Порядок секцій
  public characteristicSections = [
    {key: 'liters', name: 'Обʼєм'},
    {key: 'viscosity', name: 'Вʼязкість'},
    {key: 'approvals', name: 'Допуски OEM'},
    {key: 'class_api', name: 'Клас API'},
    {key: 'class_acea', name: 'Клас ACEA'},
    {key: 'composition', name: 'Склад'},
    {key: 'class_ilsac', name: 'Клас ILSAC'},
    {key: 'class_jaso', name: 'Клас JASO'},
  ];
  public characteristics: CharacteristicsData;
  //Расскрыть секции характеристик по ключю
  public openCharacteristics: { [key: string]: boolean } = {};
  private characteristicsAutoOpened: boolean = false;
  public approvalGroups: { name: string, count: number, values: CharacteristicItem[], isOpenGroup: boolean, hasSelected: boolean }[] = [];
  public filterApproval: string = '';
  public showAllApprovals: boolean = false;
  public approvalGroupsLimit: number = 8;
  public flatValues: { [key: string]: CharacteristicItem[] } = {};
  public showAllFlat: { [key: string]: boolean } = {};
  public flatValuesLimit: number = 10;
  private haveGoods: number = 0;
  private characteristicsHaveGoods: number | null = null;

  foundArray;
  categoriesSorted;
  public brandsSortedOrigin: Brand[];
  public foundArrayBrands: Brand[];
  isBrandsMenuShow: boolean = false;
  categoryIndex: number = -1;
  public brandIndex: number = -1;
  filterBrand: string = '';
  isDarkBackground: boolean = false;
  favoritesCount: number = 1;

  ngOnInit() {
    this.refreshMenuData(this.categoriesSortedDeep, this.brandsSorted);

    // Сначала store, потом организации: обе подписки срабатывают синхронно, и если организации
    // уже загружены, loadCharacteristics() уходил с haveGoods = 0 по умолчанию, а затем ещё раз
    // с актуальным значением из store — два запроса accounts/characteristics при включённой «Наявності»
    this.store.select('haveGoodsStore')
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(val => {
        this.haveGoods = val ? 1 : 0;
        this.loadCharacteristics();
      });

    this.organizationsService.organizations$
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((organizations: Organization[]) => {
        if (!organizations || !organizations.length) {
          return;
        }

        this.activeOrganization = organizations.find((org) => org.is_active === '1' || org.is_active === true);

        if (this.activeOrganization && this.activeOrganization.type !== 'fiz') {
          this.loadBuBalance();
        } else {
          this.buBalance = null;
          this.loadCharacteristics();
        }
      });
  }

  loadCharacteristics() {
    if (!this.activeOrganization || this.activeOrganization.type !== 'fiz') {
      return;
    }

    if (this.characteristicsHaveGoods === this.haveGoods) {
      return;
    }

    this.characteristicsHaveGoods = this.haveGoods;

    this.http.post<Characteristics>(this.apiRoot + 'accounts/characteristics', {have_goods: this.haveGoods})
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(data => {
        if (data.success) {
          this.characteristics = data.data;
          this.currentCharacteristicSelect();
          this.searchApproval();
          this.buildFlatCharacteristics();
        }
      });
  }

  get showCharacteristics(): boolean {
    return !!this.characteristics && !!this.activeOrganization && this.activeOrganization.type === 'fiz';
  }

  getSelectedCharacteristics(key: string): string[] {
    const value = this.urlParams[key];

    return value ? String(value).split(',').filter(val => val !== '') : [];
  }

  isCharacteristicSelected(key: string, value: string): boolean {
    return this.getSelectedCharacteristics(key).includes(value);
  }

  //Один раз за жизнь компанента (тоесть после F5) расскрывает секцию с выбором; дальше состояние меняет только пользователь
  currentCharacteristicSelect() {
    if (this.characteristicsAutoOpened) {
      return;
    }

    this.characteristicsAutoOpened = true;

    this.characteristicSections.forEach(section => {
      if (this.getSelectedCharacteristics(section.key).length) {
        this.openCharacteristics[section.key] = true;
      }
    });
  }

  openCharacteristicHead(key: string) {
    this.openCharacteristics[key] = !this.openCharacteristics[key];
  }

  isCharacteristicOpen(key: string): boolean {
    return !!this.openCharacteristics[key];
  }

  searchApproval() {
    const filter = this.filterApproval.toLowerCase().trim();
    const selected = this.getSelectedCharacteristics('approvals');
    const openGroups = this.approvalGroups.filter(group => group.isOpenGroup).map(group => group.name);
    const groups: { [name: string]: { name: string, count: number, values: CharacteristicItem[], isOpenGroup: boolean, hasSelected: boolean } } = {};

    ((this.characteristics && this.characteristics.approvals) || []).forEach(item => {
      //Запятая разделяет значение в CSV урла - такое значение нельзя выбрать, поэтому не показываем
      if (item.value.includes(',')) {
        return;
      }

      const brand = (item.brand || '').trim();

      if (filter !== '' && !item.value.toLowerCase().includes(filter) && !brand.toLowerCase().includes(filter)) {
        return;
      }

      const name = brand || 'Інше';

      if (!groups[name]) {
        groups[name] = {name, count: 0, values: [], isOpenGroup: openGroups.includes(name), hasSelected: false};
      }

      groups[name].count += item.count;
      groups[name].values.push(item);

      if (selected.includes(item.value)) {
        groups[name].hasSelected = true;
      }
    });

    this.approvalGroups = Object.keys(groups).map(name => groups[name]);

    //Порядок допусков в группе - от бэкенда: выбор не изменяет расположение значения (в отличие от плоских секций).
    //«Другое» всегда последнее, остальные группы по количеству товаров - выбор не влияет и на порядок групп.
    this.approvalGroups.sort((a, b) => {
      if (a.name === 'Інше') {
        return 1;
      }
      if (b.name === 'Інше') {
        return -1;
      }

      return b.count - a.count;
    });
  }

  get visibleApprovalGroups() {
    if (this.showAllApprovals || this.filterApproval.trim() !== '') {
      return this.approvalGroups;
    }

    return this.approvalGroups.filter((group, i) => i < this.approvalGroupsLimit || group.hasSelected);
  }

  get hiddenApprovalGroupsCount(): number {
    return this.approvalGroups.length - this.visibleApprovalGroups.length;
  }

  isApprovalGroupOpen(group): boolean {
    return group.isOpenGroup || group.hasSelected || this.filterApproval.trim() !== '';
  }

  openApprovalGroup(group) {
    group.isOpenGroup = !group.isOpenGroup;
  }

  crossClearApproval() {
    this.filterApproval = '';
    this.searchApproval();
  }

  toggleShowAllApprovals() {
    this.showAllApprovals = !this.showAllApprovals;
  }

  buildFlatCharacteristics() {
    this.characteristicSections.forEach(section => {
      if (section.key === 'approvals') {
        return;
      }

      const selected = this.getSelectedCharacteristics(section.key);
      let values: CharacteristicItem[] = ((this.characteristics && this.characteristics[section.key]) || [])
        //Бекенд шлет объем числом (4, 0.5, 20) — в URL и в сравнениях работаем со строкой
        .map(item => ({...item, value: String(item.value)}))
        .filter(item => !item.value.includes(','));

      if (section.key === 'liters') {
        //Как у v2: «20» -> «20 л», «0.5» -> «0.5 л», по возростанию объема
        values = values
          .filter(item => Number(item.value) > 0)
          .map(item => ({...item, label: `${Number(Number(item.value).toFixed(3))} л`}))
          .sort((a, b) => Number(a.value) - Number(b.value));
      } else if (section.key === 'viscosity') {
        //Как у v2: природний порядок 0W-20, 5W-30, 10W-40, 80W…
        values = [...values].sort((a, b) => a.value.localeCompare(b.value, 'uk', {numeric: true, sensitivity: 'base'}));
      }
      // Избранные – вверх, чтобы не исчезали после «Свернуть»; остальные — в отсортированном/бэкендовом порядке
      this.flatValues[section.key] = [
        ...values.filter(item => selected.includes(item.value)),
        ...values.filter(item => !selected.includes(item.value)),
      ];
    });
  }

  hasCharacteristicValues(key: string): boolean {
    if (key === 'approvals') {
      return !!(this.characteristics && this.characteristics.approvals && this.characteristics.approvals.length);
    }

    return !!(this.flatValues[key] && this.flatValues[key].length);
  }

  toggleShowAllFlat(key: string) {
    this.showAllFlat[key] = !this.showAllFlat[key];
  }

  toggleCharacteristic(key: string, value: string) {
    if (value.includes(',')) {
      return;
    }

    let selected = this.getSelectedCharacteristics(key);

    if (selected.includes(value)) {
      selected = selected.filter(val => val !== value);
    } else {
      selected.push(value);
    }

    this.urlParams[key] = selected.join(',');
    this.applyCharacteristics();
  }

  get hasSelectedCharacteristics(): boolean {
    return this.characteristicSections.some(section => this.getSelectedCharacteristics(section.key).length > 0);
  }
  // Сбрасывает выбор одной секции, остальные характеристики и фильтры каталога остаются
  resetCharacteristic(key: string) {
    this.urlParams[key] = '';
    this.applyCharacteristics();
  }

  //Сбрасывает все 8 характеристик вместе с локальным состоянием блока (поиск допусков, «Показать все»)
  resetCharacteristics() {
    this.characteristicSections.forEach(section => {
      this.urlParams[section.key] = '';
    });

    this.filterApproval = '';
    this.showAllApprovals = false;
    this.showAllFlat = {};

    this.applyCharacteristics();
  }

  //Любое изменение характеристик: первая страница, без выбранного товара и даты поступления
  private applyCharacteristics() {
    this.urlParams.page = 1;
    this.urlParams.good_id = 0;
    this.urlParams.receipt_date = '';

    this.router.navigate(['account'], {queryParams: this.urlParams});
  }

  loadBuBalance() {
    this.http.get<BuCalculationsBalance>(this.apiRoot + 'bu_accounts/calculations')
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(data => {
        if (data.success) {
          this.buBalance = data.balance;
        }
      });
  }

  openBuCalculations() {
    this.modalBuCalculationsRef.openModal();
    this.isBrandsMenuShow = false;
    this.isDarkBackground = false;
  }

  //TODO При выборе бренда дергает accounts/data что бы обновить дерево категорий, получаем обновленный categoriesSortedDeep и brandsSorted
  refreshMenuData(categories, brands) {
    this.categoriesSortedDeep = categories;
    this.brandsSorted = brands;

    this.categoriesSorted = cloneDeep(categories);
    this.brandsSortedOrigin = cloneDeep(brands);

    this.searchBrand(false);
    this.currentCharacteristicSelect();
    this.searchApproval();
    this.buildFlatCharacteristics();
  }

  showModalPayment() {
    this.showModalPaymentRef.emit();
    this.isBrandsMenuShow = false;
    this.isDarkBackground = false;
  }

  openCategoriesHead(i) {
    this.urlParams.page = 1;
    this.urlParams.search = '';
    this.urlParams.receipt_date = '';

    if (this.categoryIndex === i) {
      this.urlParams.category_id = 0;
      this.categoryIndex = -1;
      this.categoryHeadShow = '';
      this.headCategoryName = 'Головна';
      this.subHeadCategoryName = '';
      this.headCategoryNameColored = '';

      this.router.navigate(['account'], {queryParams: this.urlParams});
      this.buildCategoriesMenuGoodsData.emit();
      return;
    }

    this.urlParams.category_id = this.foundArray[i].id;
    this.categoryHeadShow = this.foundArray[i].name;
    this.favoritesCount = this.foundArray[i].count;
    this.categoryIndex = i;
    this.headCategoryName = 'Головна';
    this.subHeadCategoryName = ' / ';
    this.headCategoryNameColored = this.foundArray[i].name;

    this.router.navigate(['account'], {queryParams: this.urlParams});
    this.buildCategoriesMenuGoodsData.emit();
  }

  searchBrand(needClear) {
    const categories = cloneDeep(this.categoriesSorted);
    const brands = this.brandsSorted;

    if (needClear) {
      this.urlParams.good_id = 0;
    }

    if (this.filterBrand === '') {
      this.foundArray = categories;
      this.foundArrayBrands = [...brands];
      this.currentBrandSelect();
      this.currentCategorySelect();
      return;
    }

    this.brandIndex = -1;
    this.foundArrayBrands = [];

    brands.forEach(brand => {
      if (((brand.name && brand.name.toLowerCase().includes(this.filterBrand.toLowerCase())) || (brand.search && brand.search.toLowerCase().includes(this.filterBrand.toLowerCase()))) && parseInt(brand.goods_count, 10) > 0) {
        this.foundArrayBrands.push(brand);
      }
    });

    this.foundArray = cloneDeep(categories);

    this.foundArray.forEach((category, headIndex) => {
      this.foundArray[headIndex].values = [];

      categories[headIndex].values.forEach(brand => {
        if ((this.urlParams.brand_id && parseInt(this.urlParams.brand_id, 10) > 0
              ? brand.name && brand.name.toLowerCase().trim() === this.filterBrand.toLowerCase().trim()
              : brand.name && brand.name.toLowerCase().includes(this.filterBrand.toLowerCase())) || (this.urlParams.brand_id && parseInt(this.urlParams.brand_id, 10) > 0
              ? brand.search && brand.search.toLowerCase().split(',').map(val => val.trim()).includes(this.filterBrand.toLowerCase().trim())
              : brand.search && brand.search.toLowerCase().includes(this.filterBrand.toLowerCase())))
        {
          this.foundArray[headIndex].values.push(brand);
          this.foundArray[headIndex].values[this.foundArray[headIndex].values.length - 1].isOpenCategory = true;
        }

        if (brand.values && brand.values.length) {
          const brandsList = [];

          brand.values.forEach(sub_brand => {
            if ((this.urlParams.brand_id && parseInt(this.urlParams.brand_id, 10) > 0
                  ? sub_brand.name && sub_brand.name.toLowerCase().trim() === this.filterBrand.toLowerCase().trim()
                  : sub_brand.name && sub_brand.name.toLowerCase().includes(this.filterBrand.toLowerCase())) || (this.urlParams.brand_id && parseInt(this.urlParams.brand_id, 10) > 0
                  ? sub_brand.search && sub_brand.search.toLowerCase().split(',').map(val => val.trim()).includes(this.filterBrand.toLowerCase().trim())
                  : sub_brand.search && sub_brand.search.toLowerCase().includes(this.filterBrand.toLowerCase())))
            {
              brandsList.push(sub_brand);
            }
          });

          if (brandsList.length) {
            if (this.foundArray[headIndex].values.length === 0 || this.foundArray[headIndex].values[this.foundArray[headIndex].values.length - 1].name !== brand.name) {
              this.foundArray[headIndex].values.push(brand);
            }

            this.foundArray[headIndex].values[this.foundArray[headIndex].values.length - 1].values = brandsList;
            this.foundArray[headIndex].values[this.foundArray[headIndex].values.length - 1].isOpenCategory = true;
          }
        }
      });
    });

    this.foundArray = this.foundArray.filter(val => val.values && val.values.length);

    this.currentCategorySelect();
  }

  crossClears() {
    this.filterBrand = '';
    this.searchBrand(false);
  }

  selectBrand(i: number) {
    const brand = this.foundArrayBrands[i];

    this.brandIndex = i;
    this.filterBrand = '';
    this.urlParams.good_id = 0;
    this.urlParams.brand_id = brand.id;
    this.urlParams.category_id = 0;
    this.categoryIndex = -1;
    this.categoryHeadShow = '';
    this.urlParams.page = 1;
    this.urlParams.search = '';
    this.urlParams.receipt_date = '';
    this.headCategoryName = 'Головна';
    this.subHeadCategoryName = ' / ' + 'Бренд' + ' / ';
    this.headCategoryNameColored = brand.name;

    this.currentBrandSelect();

    this.router.navigate(['account'], {queryParams: this.urlParams});

    this.closeModal();
  }

  currentBrandSelect() {
    const brandId = Number(this.urlParams.brand_id);

    this.foundArrayBrands = cloneDeep(this.brandsSortedOrigin);
    this.brandIndex = -1;

    if (!brandId) {
      return;
    }

    const selectedBrand = this.brandsSortedOrigin.find(brand => Number(brand.id) === brandId);

    if (!selectedBrand) {
      return;
    }

    this.foundArrayBrands = [selectedBrand];
    this.brandIndex = 0;

    const brandName = selectedBrand.name?.toLowerCase().trim() || '';

    this.foundArray = cloneDeep(this.categoriesSortedDeep);

    this.foundArray.forEach((headCategory, headIndex) => {
      this.foundArray[headIndex].values = [];

      this.categoriesSortedDeep[headIndex].values.forEach(subCategory => {
        const subCategoryFind = (subCategory.name && subCategory.name.toLowerCase().trim() === brandName) || (subCategory.search && subCategory.search.toLowerCase().split(',').map(val => val.trim()).includes(brandName));
        const subCategoryValuesFind = (subCategory.values || []).filter(subCategoryValues => (subCategoryValues.name && subCategoryValues.name.toLowerCase().trim() === brandName) || (subCategoryValues.search && subCategoryValues.search.toLowerCase().split(',').map(val => val.trim()).includes(brandName))
        );

        if (subCategoryFind || subCategoryValuesFind.length) {
          const category = cloneDeep(subCategory);
          category.isOpenCategory = true;

          if (subCategoryValuesFind.length) {
            category.values = subCategoryValuesFind;
          }

          this.foundArray[headIndex].values.push(category);
        }
      });
    });

    this.foundArray = this.foundArray.filter(category => category.values && category.values.length);
  }

  brandMenuShow() {
    this.isBrandsMenuShow = true;
    this.isDarkBackground = true;
  }

  currentCategorySelect() {
    let categoryId = this.urlParams.category_id;

    this.categoryIndex = -1;

    if (this.filterBrand === '' && !this.urlParams.brand_id) {
      this.foundArray.forEach(category => this.closeCategoryBranch(category.values));
    }

    if (!categoryId || categoryId <= 0) {
      return;
    }

    this.categoryIndex = this.foundArray.findIndex(val => val.id == categoryId);

    if (this.categoryIndex !== -1) {
      return;
    }

    // Розкриваємо всю гілку до вибраної категорії — на будь-якій глибині,
    // а не тільки до третього рівня
    this.foundArray.forEach((category, index) => {
      const path = this.menuService.findCategoryPath(category.values, categoryId);

      if (!path.length) {
        return;
      }

      path.forEach(node => node.isOpenCategory = true);
      this.categoryIndex = index;
    });
  }

  private closeCategoryBranch(branch) {
    if (!branch || !branch.length) {
      return;
    }

    branch.forEach(category => {
      category.isOpenCategory = false;
      this.closeCategoryBranch(category.values);
    });
  }

  // Клік по категорії будь-якого рівня нижче розділу. path — гілка від категорії
  // другого рівня до вибраного вузла включно, тому глибина дерева не обмежена:
  // раніше під це було два окремі обробники (другий і третій рівні).
  selectCategoryBranch(path, headIndex: number) {
    const category = path[path.length - 1];

    this.urlParams.good_id = 0;
    this.urlParams.category_id = category.id;
    this.urlParams.page = 1;
    this.urlParams.search = '';
    this.urlParams.receipt_date = '';
    this.categoryIndex = headIndex;

    category.isOpenCategory = !category.isOpenCategory;

    this.router.navigate(['account'], {queryParams: this.urlParams});

    const headName = this.categoryHeadShow === ''
      ? (this.foundArray && this.foundArray[headIndex] ? this.foundArray[headIndex].name : '')
      : this.categoryHeadShow;
    const parentNames = path.slice(0, -1).map(node => node.name);

    this.headCategoryName = 'Головна';
    this.subHeadCategoryName = ' / ' + [headName, ...parentNames].filter(name => name).join(' / ') + ' / ';
    this.headCategoryNameColored = category.name;

    if (!category.values || category.values.length === 0) {
      this.closeModal();
    }
  }

  // Шлях для дочірнього вузла — рекурсивний шаблон передає його наступному рівню
  childCategoryPath(path, category) {
    return [...path, category];
  }

  crossClearBrand(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }

    this.brandIndex = -1;
    this.urlParams.brand_id = 0;
    this.urlParams.page = 1;
    this.urlParams.search = '';
    this.urlParams.receipt_date = '';
    this.urlParams.good_id = 0;
    this.urlParams.category_id = 0;

    this.filterBrand = '';

    this.foundArrayBrands = cloneDeep(this.brandsSortedOrigin);
    this.foundArray = cloneDeep(this.categoriesSortedDeep);

    this.router.navigate(['account'], { queryParams: this.urlParams });

    this.currentCategorySelect();
  }

  closeModal() {
    this.isBrandsMenuShow = false;
    this.isDarkBackground = false;
  }

  protected readonly parseFloat = parseFloat;

  ngOnDestroy() {
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }
}
